// components/TransferApproval.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingTransfers} from '../../services/transferService';
// import { getPendingTransfers, approveTransfer, rejectTransfer } from '../../services/transferService';
import { CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';

interface TransferApprovalProps {}

const TransferApproval: React.FC<TransferApprovalProps> = () => {
  const { user } = useAuth();
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadPendingTransfers = async () => {
    setLoading(true);
    try {
      const response = await getPendingTransfers();
      setPendingTransfers(response.data);
    } catch (error: any) {
      console.error('Error loading pending transfers:', error);
      setMessage({ type: 'error', text: 'Error loading pending transfers' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingTransfers();
  }, []);

  // const handleApprove = async (reference: string) => {
  //   setActionLoading(reference);
  //   setMessage(null);
  //   try {
  //     await approveTransfer({
  //       reference,
  //       approved_by: user?.username || 'admin'
  //     });
  //     setMessage({ type: 'success', text: 'Transfer approved successfully' });
  //     await loadPendingTransfers(); // Recharger la liste
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.detail || 'Error approving transfer';
  //     setMessage({ type: 'error', text: errorMessage });
  //   } finally {
  //     setActionLoading(null);
  //   }
  // };

  // const handleReject = async (reference: string) => {
  //   setActionLoading(reference);
  //   setMessage(null);
  //   try {
  //     await rejectTransfer({
  //       reference,
  //       rejected_by: user?.username || 'admin'
  //     });
  //     setMessage({ type: 'success', text: 'Transfer rejected successfully' });
  //     await loadPendingTransfers(); // Recharger la liste
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.detail || 'Error rejecting transfer';
  //     setMessage({ type: 'error', text: errorMessage });
  //   } finally {
  //     setActionLoading(null);
  //   }
  // };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'OK':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'INSUFFICIENT':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'wallet_to_wallet':
        return 'Wallet to Wallet';
      case 'bank_to_wallet':
        return 'Bank to Wallet';
      case 'wallet_to_bank':
        return 'Wallet to Bank';
      default:
        return type;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pending Transfers Approval</h1>
              <p className="text-gray-600 mt-1">Approve or reject transfer requests from merchants</p>
            </div>
            <button
              onClick={loadPendingTransfers}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {message && (
          <div className={`mx-6 mt-4 p-3 rounded-lg flex items-center ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-gray-600">Loading pending transfers...</p>
              </div>
            </div>
          ) : pendingTransfers.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Transfers</h3>
              <p className="text-gray-600">All transfer requests have been processed.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Merchant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transfer.reference}</div>
                        <div className="text-sm text-gray-500">{transfer.method}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transfer.merchant_code}</div>
                        <div className="text-sm text-gray-500">{transfer.institution}</div>
                        <div className="text-xs text-gray-400">By: {transfer.initiated_by}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{getTypeLabel(transfer.transfer_type)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transfer.amount.toLocaleString()} {transfer.currency}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(transfer.balance_status)}>
                          {transfer.balance_status}
                        </span>
                        {transfer.balance_status === 'OK' && (
                          <div className="text-xs text-gray-500 mt-1">
                            Deposit: {transfer.balance_deposit?.toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transfer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {/* <button
                            onClick={() => handleApprove(transfer.reference)}
                            disabled={actionLoading === transfer.reference || transfer.balance_status === 'INSUFFICIENT'}
                            className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                          >
                            {actionLoading === transfer.reference ? (
                              <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(transfer.reference)}
                            disabled={actionLoading === transfer.reference}
                            className="flex items-center px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                          >
                            {actionLoading === transfer.reference ? (
                              <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <XCircle className="h-3 w-3 mr-1" />
                            )}
                            Reject
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferApproval;